import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  IconButton,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  useTheme,
  alpha,
  Menu,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import {
  Edit as EditIcon,
  Archive as ArchiveIcon,
  Restore as RestoreIcon,
  Add as AddIcon,
  Search as SearchIcon,
  Inventory as InventoryIcon,
  Visibility as VisibilityIcon,
  FilterList as FilterIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
  Category as CategoryIcon,
  Numbers as NumbersIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  ClearAll as ClearAllIcon,
  FileDownload as DownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as CsvIcon,
  Assessment as ReportIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import ListFilterBar from '../../common/ListFilterBar';
import Header from '../../common/Header';
import Loading from '../../common/Loading';
import FeedbackMessage from '../../common/FeedbackMessage';
import StatCards from '../../common/StatCards';
import TableList from '../../common/TableList';
import ListBulkToolbar from '../../common/ListBulkToolbar';
import { STOCK_BULK_CONFIG } from '../../../utils/bulkUpdateConfigs';
import { useListBulkSelection, bulkTableProps, createBulkHandlers } from '../../../utils/listBulkSelection';

import ConfirmDeleteDialog from '../../common/ConfirmDeleteDialog';
import api from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { canManageInventory, canDeleteStock } from '../../../utils/inventoryPermissions';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import dayjs from 'dayjs';
import { SUCCESS } from '../../common/SaaSTokens';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

const StockList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const { user: currentUser } = useAuth();
const { selectedIds, setSelectedIds, clearSelection } = useListBulkSelection();
  
  
  const [stocks, setStocks] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    department: 'all',
    status: 'all'
  });
  const [activeStatFilter, setActiveStatFilter] = useState('all');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedStock, setSelectedStock] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [reportMenuAnchor, setReportMenuAnchor] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);

  const canManageStock = canManageInventory(currentUser);

  const filterOptions = [
    {
      name: 'department',
      label: 'Department',
      type: 'select',
      options: [
        { value: 'all', label: 'All Departments' },
        ...departments.map(dept => ({
          value: dept.id.toString(),
          label: dept.name
        }))
      ]
    },
    {
      name: 'status',
      label: 'Inventory Status',
      type: 'select',
      options: [
        { value: 'all', label: 'All Status' },
        { value: 'in_stock', label: 'Available' },
        { value: 'low_stock', label: 'Low Inventory' },
        { value: 'out_of_stock', label: 'Out of Inventory' },
        { value: 'archived', label: 'Archived' },
      ]
    }
  ];

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!canManageStock) {
      navigate('/dashboard');
      return;
    }
    fetchStocks({ forceRefresh: Boolean(location.state?.refreshList) });
    fetchDepartments();
  }, [currentUser, location.pathname, location.state?.refreshList]);

  const fetchStocks = async ({ forceRefresh = false, archived = false } = {}) => {
    try {
      setLoading(true);
      const response = await api.get('/stocks', {
        ...(forceRefresh ? { cache: false } : {}),
        ...(archived ? { params: { archived: 1 } } : {}),
      });
      setStocks(Array.isArray(response.data) ? response.data.filter(Boolean) : []);
      setError(null);
    } catch (error) {
      console.error('Error fetching stocks:', error);
      setError('Failed to load stock items. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/departments');
      setDepartments(response.data);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const handleDelete = (stock) => {
    setSelectedStock(stock);
    setDeleteDialogOpen(true);
  };

  const showAllItems = () => {
    setFilters((current) => ({ ...current, status: 'all' }));
    setActiveStatFilter('all');
    setPage(0);
  };

  const confirmDelete = async () => {
    if (!selectedStock?.id) return;

    const stockId = selectedStock.id;
    const stockName = selectedStock.things || 'Inventory item';

    try {
      setDeleteLoading(true);
      await api.post(`/stocks/${stockId}/archive`);
      await fetchStocks({ forceRefresh: true });
      showAllItems();
      setDeleteDialogOpen(false);
      setSelectedStock(null);
      setSuccess(`Inventory item "${stockName}" has been archived successfully!`);
    } catch (error) {
      console.error('Error archiving stock:', error);
      const errorMessage = error.response?.data?.message || 'Failed to archive stock item. Please try again.';
      setError(errorMessage);
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleRestore = async (stock) => {
    try {
      await api.post(`/stocks/${stock.id}/restore`);
      setStocks((items) => items.filter((item) => item.id !== stock.id));
      setSuccess(`Inventory item "${stock.things || 'Inventory item'}" has been restored successfully!`);
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to restore stock item. Please try again.');
    }
  };

  const handleBulkArchive = async () => {
    const ids = [...(selectedIds || [])];
    if (!ids.length) {
      setError('No selected stock items to archive.');
      return;
    }

    try {
      await Promise.all(ids.map((stockId) => api.post(`/stocks/${Number(stockId)}/archive`)));
      const idSet = new Set(ids.map(String));
      setStocks((items) => items.filter((item) => !idSet.has(String(item.id))));
      clearSelection();
      setError(null);
      setSuccess(`${ids.length} stock item(s) archived successfully.`);
      await fetchStocks({ forceRefresh: true });
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to archive selected stock items. Please try again.');
      throw error;
    }
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleFilterChange = (newFilters) => {
    const archivedViewChanged = (filters.status === 'archived') !== (newFilters.status === 'archived');
    const nextActive = newFilters.status !== 'all' ? newFilters.status : 'all';
    setActiveStatFilter(nextActive);
    setFilters(newFilters);
    setPage(0);
    if (archivedViewChanged) {
      clearSelection();
      fetchStocks({ forceRefresh: true, archived: newFilters.status === 'archived' });
    }
  };

  const handleClearFilters = () => {
    const wasArchived = filters.status === 'archived';
    setFilters({ department: 'all', status: 'all' });
    setActiveStatFilter('all');
    setSearchTerm('');
    setPage(0);
    if (wasArchived) fetchStocks({ forceRefresh: true });
  };

  const handleStatCardClick = (_, index) => {
    const filterMap = ['all', 'in_stock', 'low_stock', 'out_of_stock'];
    const selectedFilter = filterMap[index] || 'all';
    const nextFilter = activeStatFilter === selectedFilter ? 'all' : selectedFilter;
    setActiveStatFilter(nextFilter);

    const nextFilters = { ...filters };
    if (nextFilter === 'all') {
      nextFilters.status = 'all';
    } else {
      nextFilters.status = nextFilter;
    }
    setFilters(nextFilters);
    setPage(0);
  };

  const activeStatIndex =
    activeStatFilter === 'in_stock' ? 1 :
    activeStatFilter === 'low_stock' ? 2 :
    activeStatFilter === 'out_of_stock' ? 3 :
    activeStatFilter === 'archived' ? -1 :
    0;

  const getDepartmentName = (departmentId) => {
    const department = departments.find(d => d.id === departmentId);
    return department ? department.name : 'Unknown';
  };

  const getQuantityColor = (quantity) => {
    if (quantity === 0) return 'error';
    if (quantity <= 5) return 'warning';
    if (quantity <= 20) return 'info';
    return 'success';
  };

  const getQuantityLabel = (quantity) => {
    if (quantity === 0) return 'Out of Inventory';
    if (quantity <= 5) return 'Low Inventory';
    if (quantity <= 20) return 'Medium Inventory';
    return 'Available';
  };

  const getQuantityIcon = (quantity) => {
    if (quantity === 0) return <ErrorIcon fontSize="small" />;
    if (quantity <= 5) return <WarningIcon fontSize="small" />;
    return <CheckCircleIcon fontSize="small" />;
  };

  const filteredStocks = stocks.filter(Boolean).filter(stock => {
    const itemName = String(stock.things || '').toLowerCase();
    const itemPlace = String(stock.place || '').toLowerCase();
    const itemRemark = String(stock.remark || '').toLowerCase();
    const query = searchTerm.toLowerCase();
    const matchesSearch = 
      itemName.includes(query) ||
      itemPlace.includes(query) ||
      itemRemark.includes(query);
    
    const matchesDepartment = filters.department === 'all' || stock.department_id === parseInt(filters.department);
    
    const matchesStatus = filters.status === 'all' ||
      (filters.status === 'archived' && Boolean(stock.archived_at)) ||
      (!stock.archived_at && filters.status === 'out_of_stock' && stock.quantity === 0) ||
      (!stock.archived_at && filters.status === 'low_stock' && stock.quantity > 0 && stock.quantity <= 5) ||
      (!stock.archived_at && filters.status === 'in_stock' && stock.quantity > 5);
      
    return matchesSearch && matchesDepartment && matchesStatus;
  }).sort((a, b) => {
    const archivedOrder = Number(Boolean(a.archived_at)) - Number(Boolean(b.archived_at));
    if (archivedOrder !== 0) return archivedOrder;
    return Number(b.id || 0) - Number(a.id || 0);
  });

  const isReportMenuOpen = Boolean(reportMenuAnchor);

  const closeReportMenu = () => setReportMenuAnchor(null);

  const openReportMenu = (event) => {
    setReportMenuAnchor(event.currentTarget);
  };

  const buildReportRows = () => (
    filteredStocks.map((stock, index) => ({
      no: index + 1,
      item: stock.things || '-',
      location: stock.place || '-',
      department: getDepartmentName(stock.department_id),
      totalQuantity: Number(stock.quantity || 0),
      availableQuantity: Number(stock.available_quantity || 0),
      borrowedQuantity: Number(stock.currently_borrowed || 0),
      status: stock.archived_at ? 'Archived' : getQuantityLabel(stock.quantity || 0),
      remark: stock.remark || '-',
    }))
  );

  const generateCsvReport = () => {
    const reportRows = buildReportRows();
    const reportDate = dayjs().format('YYYY-MM-DD HH:mm');
    const filtersSummary = `Department: ${
      filters.department === 'all' ? 'All Departments' : getDepartmentName(parseInt(filters.department, 10))
    } | Status: ${
      filters.status === 'all' ? 'All Status' : filters.status.replace(/_/g, ' ')
    } | Search: ${searchTerm || 'None'}`;

    const headers = [
      'No',
      'Item',
      'Location',
      'Department',
      'Total Quantity',
      'Available Quantity',
      'Borrowed Quantity',
      'Status',
      'Remark',
    ];

    const quoteCsv = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const summaryRows = [
      ['STOCK REPORT'],
      ['Generated At', reportDate],
      ['Generated By', currentUser?.name || 'System'],
      ['Applied Filters', filtersSummary],
      ['Total Records', reportRows.length],
      [''],
    ];

    const csvRows = reportRows.map((row) => [
      row.no,
      quoteCsv(row.item),
      quoteCsv(row.location),
      quoteCsv(row.department),
      row.totalQuantity,
      row.availableQuantity,
      row.borrowedQuantity,
      quoteCsv(row.status),
      quoteCsv(row.remark),
    ]);

    const csvContent = [...summaryRows, headers, ...csvRows]
      .map((row) => row.join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.setAttribute('download', `Stock_Report_${dayjs().format('YYYYMMDD_HHmmss')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(link.href);
  };

  const generatePdfReport = () => {
    const reportRows = buildReportRows();
    const doc = new jsPDF({ unit: 'pt', format: 'a4' });
    const marginX = 36;
    const pageWidth = doc.internal.pageSize.getWidth();
    const baseTable = {
      styles: {
        font: 'helvetica',
        fontSize: 9,
        cellPadding: 5,
        overflow: 'linebreak',
        textColor: [30, 30, 30],
      },
      headStyles: {
        fillColor: [20, 55, 90],
        textColor: 255,
        fontStyle: 'bold',
        fontSize: 9,
      },
      margin: { left: marginX, right: marginX },
      tableWidth: pageWidth - marginX * 2,
      theme: 'striped',
      alternateRowStyles: { fillColor: [248, 250, 252] },
    };

    let y = 38;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(15);
    doc.text('Inventory Report', pageWidth / 2, y, { align: 'center' });

    y += 22;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text(`Generated: ${dayjs().format('YYYY-MM-DD HH:mm')}`, marginX, y);
    y += 14;
    doc.text(`Generated by: ${currentUser?.name || 'System'}`, marginX, y);
    y += 14;
    doc.text(`Records: ${reportRows.length}`, marginX, y);
    y += 18;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(20, 55, 90);
    doc.text('Applied filters', marginX, y);
    y += 16;

    autoTable(doc, {
      ...baseTable,
      startY: y,
      head: [['Field', 'Value']],
      body: [
        ['Department', filters.department === 'all' ? 'All Departments' : getDepartmentName(parseInt(filters.department, 10))],
        ['Status', filters.status === 'all' ? 'All Status' : filters.status.replace(/_/g, ' ')],
        ['Search', searchTerm || 'None'],
      ],
    });

    y = doc.lastAutoTable.finalY + 18;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.setTextColor(20, 55, 90);
    doc.text('Inventory item details', marginX, y);
    y += 12;

    autoTable(doc, {
      ...baseTable,
      startY: y,
      head: [[
        'No',
        'Item',
        'Department',
        'Location',
        'Total',
        'Available',
        'Borrowed',
        'Status',
        'Remark',
      ]],
      body: reportRows.map((row) => [
        row.no,
        row.item,
        row.department,
        row.location,
        row.totalQuantity,
        row.availableQuantity,
        row.borrowedQuantity,
        row.status,
        row.remark,
      ]),
      columnStyles: {
        0: { cellWidth: 24, halign: 'center' },
        1: { cellWidth: 110 },
        2: { cellWidth: 72 },
        3: { cellWidth: 64 },
        4: { cellWidth: 40, halign: 'center' },
        5: { cellWidth: 50, halign: 'center' },
        6: { cellWidth: 50, halign: 'center' },
        7: { cellWidth: 64, halign: 'center' },
        8: { cellWidth: 'auto' },
      },
      didDrawPage: () => {
        const pageCount = doc.getNumberOfPages();
        const currentPage = doc.internal.getCurrentPageInfo().pageNumber;
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(8);
        doc.setTextColor(120, 120, 120);
        doc.text(
          `Page ${currentPage} of ${pageCount}`,
          pageWidth - marginX,
          doc.internal.pageSize.getHeight() - 16,
          { align: 'right' }
        );
      },
    });

    doc.save(`Stock_Report_${dayjs().format('YYYYMMDD_HHmmss')}.pdf`);
  };

  const handleGenerateReport = async (type) => {
    closeReportMenu();
    try {
      setReportLoading(true);
      if (type === 'csv') {
        generateCsvReport();
      } else {
        generatePdfReport();
      }
      setSuccess(`Inventory report (${type.toUpperCase()}) generated successfully.`);
    } catch (reportError) {
      console.error('Error generating stock report:', reportError);
      setError('Failed to generate stock report. Please try again.');
    } finally {
      setReportLoading(false);
    }
  };

  // ─── STATS CALCULATED FROM ALL DATA (NOT FILTERED) ───────────────────────────────
  const statsData = [
    { icon: InventoryIcon, value: stocks.filter(stock => stock && !stock.archived_at).length, label: 'Active Items', color: 'info' },
    { icon: CheckCircleIcon, value: stocks.filter(stock => stock && !stock.archived_at && stock.quantity > 5).length, label: 'Available', color: 'success' },
    { icon: WarningIcon, value: stocks.filter(stock => stock && !stock.archived_at && stock.quantity > 0 && stock.quantity <= 5).length, label: 'Low Inventory', color: 'warning' },
    { icon: ErrorIcon, value: stocks.filter(stock => stock && !stock.archived_at && stock.quantity === 0).length, label: 'Out of Inventory', color: 'error' },
  ];

  const stockColumns = [
    {
      key: 'things',
      label: 'Item',
      icon: CategoryIcon,
      minWidth: 260,
      render: (stock) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ width: 36, height: 36, bgcolor: theme.palette.primary.main }}>
            <CategoryIcon fontSize="small" />
          </Avatar>
          <Box>
            <Typography 
              variant="body2" 
              sx={{ 
                fontWeight: 600,
                color: theme.palette.mode === 'dark' ? '#e0e0e0' : 'inherit'
              }}
            >
              {stock.things}
            </Typography>
            {stock.remark && (
              <Typography 
                variant="caption" 
                sx={{
                  // Lighter “description/remark” tone for readability
                  color:
                    theme.palette.mode === 'dark'
                      ? alpha('#E2E8F0')
                      : alpha(theme.palette.text.primary),
                }}
              >
                {stock.remark}
              </Typography>
            )}
          </Box>
        </Box>
      ),
    },
    {
      key: 'available_quantity',
      label: 'Available',
      icon: NumbersIcon,
      minWidth: 140,
      render: (stock) => (
        <Box>
          <Typography 
            variant="body2" 
            sx={{ 
              fontWeight: 600,
              color: theme.palette.mode === 'dark' ? '#e0e0e0' : 'inherit'
            }}
          >
            {stock.available_quantity || 0}
          </Typography>
          {(stock.currently_borrowed || 0) > 0 && (
            <Typography 
              variant="caption" 
              sx={{
                color: theme.palette.mode === 'dark' ? '#a0a0a0' : 'inherit'
              }}
            >
              ({stock.currently_borrowed} borrowed)
            </Typography>
          )}
        </Box>
      ),
    },
    { 
      key: 'place', 
      label: 'Location', 
      icon: LocationIcon,
      minWidth: 160,
      render: (stock) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 600,
            color: theme.palette.mode === 'dark' ? '#e0e0e0' : 'inherit'
          }}
        >
          {stock.place}
        </Typography>
      )
    },
    {
      key: 'department_id',
      label: 'Department',
      icon: BusinessIcon,
      minWidth: 160,
      sortAccessor: (stock) => getDepartmentName(stock.department_id),
      render: (stock) => (
        <Typography 
          variant="body2" 
          sx={{ 
            fontWeight: 600,
            color: theme.palette.mode === 'dark' ? '#e0e0e0' : 'inherit'
          }}
        >
          {getDepartmentName(stock.department_id)}
        </Typography>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      minWidth: 140,
      sortAccessor: (stock) => stock.archived_at ? 'Archived' : getQuantityLabel(stock.quantity),
      render: (stock) => (
        <Chip
          icon={stock.archived_at ? <ArchiveIcon /> : getQuantityIcon(stock.quantity)}
          label={stock.archived_at ? 'Archived' : getQuantityLabel(stock.quantity)}
          color={stock.archived_at ? 'default' : getQuantityColor(stock.quantity)}
          size="small"
          sx={stock.archived_at ? {
            color: theme.palette.mode === 'dark' ? '#E2E8F0' : '#475569',
            backgroundColor: alpha('#94A3B8', theme.palette.mode === 'dark' ? 0.2 : 0.14),
            border: `1px solid ${alpha('#94A3B8', 0.45)}`,
            fontWeight: 700,
            '& .MuiChip-icon': {
              color: theme.palette.mode === 'dark' ? '#CBD5E1' : '#64748B',
            },
          } : {
            fontWeight: 600,
            color: theme.palette.mode === 'dark'
              ? getQuantityColor(stock.quantity) === 'error' ? '#fca5a5' :
                getQuantityColor(stock.quantity) === 'warning' ? '#fcd34d' :
                getQuantityColor(stock.quantity) === 'info' ? '#93c5fd' :
                '#86efac'
              : 'inherit',
          }}
        />
      ),
    },
    {
      key: 'actions',
      label: 'Actions',
      align: 'right',
      sortable: false,
      minWidth: 160,
      render: (stock) => (
        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
          <Tooltip title="View Inventory Item" arrow>
            <IconButton 
              size="small" 
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/stocks/${stock.id}`);
              }}
              sx={{
                color: theme.palette.info.main,
                '&:hover': {
                  backgroundColor: alpha(theme.palette.info.main, 0.1),
                },
              }}
            >
              <VisibilityIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canManageStock && (
            <>
              {!stock.archived_at && <Tooltip title="Edit Inventory Item" arrow>
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/stocks/edit/${stock.id}`);
                  }}
                  sx={{
                    color: theme.palette.warning.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.warning.main, 0.1),
                    },
                  }}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>}
              {stock.archived_at && (
                <Tooltip title="Restore Inventory Item" arrow>
                  <IconButton
                    size="small"
                    onClick={(e) => { e.stopPropagation(); handleRestore(stock); }}
                    sx={{ color: theme.palette.success.main }}
                  >
                    <RestoreIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              {canDeleteStock(currentUser, stock) && (
              <Tooltip title="Archive Inventory Item" arrow>
                <IconButton 
                  size="small" 
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(stock);
                  }}
                  sx={{
                    color: theme.palette.error.main,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                    },
                  }}
                >
                  <ArchiveIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              )}
            </>
          )}
        </Box>
      ),
    },
  ];

  if (loading) return(
    <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', gap: 2 }}>
      <Loading message="Loading stock list" size={56} variant="primary" />
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Header
        title="Inventory Management"
        subtitle="Manage inventory and track stock levels"
        icon={InventoryIcon}
        actionButton={(
          <Box sx={{ display: 'flex', gap: 1.5 }}>
            <Button
              variant="outlined"
              startIcon={reportLoading ? <CircularProgress size={14} color="inherit" /> : <ReportIcon />}
              onClick={openReportMenu}
              disabled={reportLoading || filteredStocks.length === 0}
              sx={{
                height: 'auto',
                borderRadius: '10px',
                px: 2.5,
                py: 1,
                fontFamily: "'DM Sans', sans-serif",
                fontWeight: 600,
                fontSize: '0.875rem',
                bgcolor: alpha('#fff', 0.1),
                color: '#fff',
                borderColor: alpha('#fff', 0.3),
                backdropFilter: 'blur(8px)',
                '&:hover': { bgcolor: alpha('#fff', 0.2), borderColor: alpha('#fff', 0.5) },
                '&.Mui-disabled': { bgcolor: alpha('#fff', 0.05), color: alpha('#fff', 0.3) },
              }}
            >
              {reportLoading ? 'Generating...' : 'Generate Report'}
            </Button>

            {canManageStock ? (
              <Button
                variant="contained"
                size="large"
                startIcon={<AddIcon />}
                onClick={() => navigate('/stocks/add')}
                sx={{
                  bgcolor: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  '&:hover': {
                    bgcolor: 'rgba(255, 255, 255, 0.3)',
                  },
                  borderRadius: 2,
                  px: 3,
                  py: 1.5
                }}
              >
                Add Inventory Item
              </Button>
            ) : null}
          </Box>
        )}
      />

      <Menu
        anchorEl={reportMenuAnchor}
        open={isReportMenuOpen}
        onClose={closeReportMenu}
        PaperProps={{
          elevation: 6,
          sx: {
            mt: 1,
            minWidth: 210,
            borderRadius: 2,
            border: `1px solid ${alpha('#38BDF8', 0.28)}`,
            background: `linear-gradient(155deg, ${alpha('#050d1a', 0.98)}, ${alpha('#0a1628', 0.95)})`,
            backdropFilter: 'blur(8px)',
            boxShadow: `0 8px 32px ${alpha('#000', 0.55)}`,
            '& .MuiMenuItem-root': {
              color: alpha('#E2E8F0', 0.9),
              borderRadius: 1.25,
              mx: 0.5,
              my: 0.25,
              '& .MuiListItemIcon-root': {
                color: alpha('#38BDF8', 0.88),
                minWidth: 32,
              },
              '& .MuiListItemText-secondary': {
                color: alpha('#94A3B8', 0.88),
              },
              '&:hover': {
                backgroundColor: alpha('#38BDF8', 0.12),
              },
              '&.Mui-selected, &.Mui-selected:hover': {
                backgroundColor: alpha('#38BDF8', 0.2),
              },
            },
          },
        }}
      >
        <MenuItem onClick={() => handleGenerateReport('csv')} disabled={reportLoading}>
          <ListItemIcon>
            <CsvIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Download CSV Report"
            secondary="Spreadsheet-compatible format"
          />
        </MenuItem>
        <MenuItem onClick={() => handleGenerateReport('pdf')} disabled={reportLoading}>
          <ListItemIcon>
            <PdfIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Download PDF Report"
            secondary="Printable report format"
          />
        </MenuItem>
      </Menu>

      {/* Error Message */}
      <FeedbackMessage
        message={error}
        severity="error"
        onClose={() => setError(null)}
        sx={{ mb: 3, fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', background: alpha('#450a0a', 0.92), border: '1px solid rgba(248,113,113,0.3)', color: '#fca5a5', boxShadow: '0 4px 20px rgba(239,68,68,0.12)' }}
      />

      {/* Success Message */}
      <FeedbackMessage
        message={success}
        severity="success"
        onClose={() => setSuccess(null)}
        sx={{ mb: 3, fontFamily: "'DM Sans',sans-serif", fontSize: '0.9rem', fontWeight: 500, background: alpha(SUCCESS, 0.12), border: `1px solid ${alpha(SUCCESS, 0.25)}`, color: alpha(SUCCESS, 0.95) }}
      />

      <StatCards stats={statsData} onCardClick={handleStatCardClick} activeIndex={activeStatIndex} />

      <ListFilterBar
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFilterChange={handleFilterChange}
        onClearFilters={handleClearFilters}
        filterOptions={filterOptions}
        searchPlaceholder="Search by item name, location, or remark..."
      />
      {filters.status !== 'archived' && <ListBulkToolbar
        config={STOCK_BULK_CONFIG}
        currentUser={currentUser}
        selectedIds={selectedIds}
        onClearSelection={clearSelection}
        {...createBulkHandlers({
          endpoint: STOCK_BULK_CONFIG.endpoint,
          setItems: setStocks,
          selectedIds,
          clearSelection,
          setSuccess,
          successLabel: 'stock item(s)',
          deleteActionPastTense: 'archived',
          refetch: async (options) => {
            await fetchStocks(options);
            showAllItems();
          },
          setError,
          filterDeleteIds: (ids) => {
            const activeIds = ids.filter((id) => {
              const stock = stocks.find((item) => String(item.id) === String(id));
              return stock && !stock.archived_at;
            });
            if (activeIds.length === 0) {
              setError('The selected stock items are already archived.');
              return false;
            }
            return activeIds;
          },
        })}
        onDeleteSuccess={handleBulkArchive}
      />}


      <TableList
        columns={stockColumns}
        data={filteredStocks}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        {...(filters.status !== 'archived'
          ? bulkTableProps(STOCK_BULK_CONFIG, currentUser, selectedIds, setSelectedIds)
          : {})}
        loading={loading}
        emptyState={{
          icon: <InventoryIcon />,
          title: 'No stock items found',
          description: searchTerm || filters.department !== 'all' || filters.status !== 'all'
            ? 'Try adjusting your search or filters.'
            : 'Get started by adding your first stock item.',
        }}
        onRowClick={(row) => navigate(`/stocks/${row.id}`)}
      />

      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setSelectedStock(null);
        }}
        onConfirm={confirmDelete}
        loading={deleteLoading}
        itemType="stock item"
        itemName={selectedStock?.things}
        title="Archive Inventory Item"
        description="The stock item will be archived, prevented from new borrows, and remain in the system for inventory history."
        confirmLabel="Archive Inventory Item"
        actionVerb="archive"
      />
    </Container>
  );
};

export default StockList;
